import { useAuth0 } from "@auth0/auth0-react";
import { Megaphone } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { useApiWithAuth } from "../../../hooks/useAuth.js";
import { IS_NATIVE_APP } from "../../../config/appTarget.js";
import { PAGE_GUTTER_CLASS } from "../../../shared/PageContainer.js";
import { useHideOnScroll } from "../../../hooks/useHideOnScroll.js";

/**
 * Floating "Anunciar no Pawseum" call-to-action shown on the landing page.
 * Fixed to the bottom-right corner (respecting the page gutter). Uses the same
 * specular button treatment as the app-download CTA — a sweeping light streak
 * plus a cursor-following highlight. Routes to the advertiser flow on click.
 * Hidden for users who already manage animals (kennel admins) or are approved
 * advertisers.
 */
const AnunciarFloater = () => {
  const { t } = useTranslation("landing");
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth0();
  const { isAdvertiser, canManageAnimals, isPermissionsLoading } =
    useApiWithAuth();
  const hidden = useHideOnScroll(1.4);

  // Only show to logged-out or non-advertiser users.
  if (
    isAuthenticated &&
    (isPermissionsLoading || canManageAnimals || isAdvertiser)
  ) {
    return null;
  }

  return (
    <>
      <style>{`
        .specular-btn::before {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: inherit;
          background: linear-gradient(100deg, transparent 35%, rgba(255,255,255,0.6) 50%, transparent 65%);
          transform: translateX(-130%) skewX(-18deg);
          animation: specularSweep 5s ease-in-out infinite;
          pointer-events: none;
        }
        .specular-btn::after {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: inherit;
          background: radial-gradient(90px circle at var(--mx, 50%) var(--my, 0%), rgba(255,255,255,0.45), transparent 60%);
          opacity: 0;
          transition: opacity .25s ease;
          pointer-events: none;
        }
        .specular-btn:hover::after { opacity: 1; }
        @keyframes specularSweep {
          0% { transform: translateX(-130%) skewX(-18deg); }
          60%, 100% { transform: translateX(160%) skewX(-18deg); }
        }
        @media (prefers-reduced-motion: reduce) {
          .specular-btn::before { animation: none; opacity: 0; }
        }
      `}</style>
      <div
        className={`fixed inset-x-0 z-40 pointer-events-none flex justify-end transition-opacity duration-500 ${hidden ? "opacity-0" : "opacity-100"} ${PAGE_GUTTER_CLASS}`}
        style={{
          bottom: IS_NATIVE_APP
            ? "calc(env(safe-area-inset-bottom) + 96px)"
            : "1.5rem",
        }}
      >
        <button
          type="button"
          onClick={() => navigate("/quero-anunciar")}
          onMouseMove={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
            e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
          }}
          aria-label={t("advertiseFloater.ariaLabel")}
          className={`specular-btn ${hidden ? "pointer-events-none" : "pointer-events-auto"} relative overflow-hidden flex items-center gap-2
            rounded-full bg-golden text-my-black font-semibold
            px-3.5 py-2 text-sm
            md:px-5 md:py-2.5
            mr-2 shadow-br-lg ring-1 ring-white/15
            focus:outline-none focus:ring-2 focus:ring-gradient-blue
            transition-transform duration-200 hover:scale-105`}
        >
          <span className="relative z-10 flex items-center gap-2">
            <Megaphone className="h-[18px] w-[18px]" aria-hidden="true" />
            <span>
              <Trans
                t={t}
                i18nKey="advertiseFloater.label"
                components={{ desktop: <span className="hidden md:inline" /> }}
              />
            </span>
          </span>
        </button>
      </div>
    </>
  );
};

export default AnunciarFloater;
